import { getAnalytics, logEvent, setAnalyticsCollectionEnabled, setConsent, type Analytics } from 'firebase/analytics'
import { app } from './firebase'
import { logger } from '@/utils/secureLogger'

export type EntityType =
    | 'storage'
    | 'product'
    | 'category'
    | 'location'
    | 'customer'
    | 'supplier'
    | 'transaction'
    | 'account'
    | 'custom_input'
    | 'access_invitation'

export type AnalyticsConsent = 'granted' | 'denied'
export type NavigationSource = 'search'
export type StockAdjustmentType = 'increment' | 'decrement' | 'adjust'

export interface AppLocationState {
    navigationSource?: NavigationSource
}

export const SEARCH_NAVIGATION_STATE: AppLocationState = {
    navigationSource: 'search',
}

const CONSENT_STORAGE_KEY = 'ordnat_analytics_consent'

const CONSENT_DENIED = {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
} as const

const CONSENT_GRANTED = {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
} as const

const STATIC_PATH_SEGMENTS = new Set([
    '',
    '401',
    'access',
    'access-invitations',
    'account',
    'avatar-settings',
    'categories',
    'claim-account',
    'complete-account',
    'create',
    'customers',
    'delete',
    'edit',
    'edit_custom_inputs',
    'faq',
    'general-settings',
    'history',
    'incoming',
    'locations',
    'login',
    'lost-password',
    'notifications',
    'outgoing',
    'pricing',
    'privacy',
    'products',
    'security-settings',
    'sign-up',
    'statistics',
    'storages',
    'suppliers',
    'terms',
    'transactions',
])

let analyticsInstance: Analytics | null = null

const sanitizeSearchTerm = (term: string): string | undefined => {
    const trimmed = term.trim().slice(0, 100)
    if (!trimmed || trimmed.includes('@')) return undefined
    return trimmed
}

const clearAnalyticsCookies = (): void => {
    if (typeof document === 'undefined') return

    document.cookie.split(';').forEach((cookie) => {
        const name = cookie.split('=')[0]?.trim()
        if (!name) return
        if (name.startsWith('_ga') || name.startsWith('_gid') || name.startsWith('_gat')) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
        }
    })
}

export const getStoredAnalyticsConsent = (): AnalyticsConsent | null => {
    try {
        const value = localStorage.getItem(CONSENT_STORAGE_KEY)
        if (value === 'granted' || value === 'denied') return value
    } catch {
        // localStorage can be unavailable in private mode
    }
    return null
}

export const hasAnalyticsConsent = (): boolean => getStoredAnalyticsConsent() === 'granted'

export const applyAnalyticsConsent = (consent: AnalyticsConsent): void => {
    try {
        localStorage.setItem(CONSENT_STORAGE_KEY, consent)
    } catch {
        // Ignore storage failures; tracking still stays off without a stored grant.
    }

    if (consent !== 'granted') {
        try {
            if (analyticsInstance) {
                setConsent(CONSENT_DENIED)
                setAnalyticsCollectionEnabled(analyticsInstance, false)
            }
        } catch (error) {
            logger.debug('Failed to disable analytics', error, 'analytics')
        }
        clearAnalyticsCookies()
        return
    }

    try {
        setConsent(CONSENT_GRANTED)
        if (!analyticsInstance) {
            analyticsInstance = getAnalytics(app)
        } else {
            setAnalyticsCollectionEnabled(analyticsInstance, true)
        }
    } catch (error) {
        logger.debug('Failed to enable analytics', error, 'analytics')
        analyticsInstance = null
    }
}

export const initAnalyticsFromStoredConsent = (): void => {
    if (getStoredAnalyticsConsent() === 'granted') {
        applyAnalyticsConsent('granted')
    }
}

export const normalizePagePath = (pathname: string): string => {
    const normalized = pathname
        .split('/')
        .map((segment) => {
            if (!segment || STATIC_PATH_SEGMENTS.has(segment)) return segment
            return ':id'
        })
        .join('/')

    return normalized || '/'
}

export const trackEvent = (
    name: string,
    params?: Record<string, string | number | boolean>
): void => {
    if (!hasAnalyticsConsent() || !analyticsInstance) return

    try {
        const eventParams = {
            ...params,
            ...(import.meta.env.VITE_MODE === 'development' ? { debug_mode: true } : {}),
        }
        logEvent(analyticsInstance, name, eventParams)
        logger.debug('Analytics event', { name, params: eventParams }, 'analytics')
    } catch (error) {
        logger.debug('Analytics event failed', error, 'analytics')
    }
}

export const trackPageView = ({
    path,
    title,
    source,
}: {
    path: string
    title?: string
    source?: NavigationSource
}): void => {
    const params: Record<string, string> = {
        page_path: normalizePagePath(path),
        page_title: title || (typeof document !== 'undefined' ? document.title : ''),
    }

    if (typeof window !== 'undefined') {
        params.page_location = `${window.location.origin}${params.page_path}`
    }

    if (source) {
        params.navigation_source = source
    }

    trackEvent('page_view', params)
}

export const trackSearch = ({
    contentType,
    searchTerm,
}: {
    contentType: string
    searchTerm: string
}): void => {
    const params: Record<string, string> = {
        content_type: contentType,
    }
    const sanitizedTerm = sanitizeSearchTerm(searchTerm)
    if (sanitizedTerm) {
        params.search_term = sanitizedTerm
    }

    trackEvent('search', params)
}

export const trackSearchNavigate = ({
    contentType,
    itemId,
}: {
    contentType: string
    itemId?: string
}): void => {
    const params: Record<string, string> = {
        content_type: contentType,
        navigation_source: 'search',
    }
    if (itemId) {
        params.item_id = itemId
    }

    trackEvent('select_content', params)
}

export const trackCreate = (entity: EntityType): void => {
    trackEvent('create_item', { item_category: entity })
}

export const trackEdit = (entity: EntityType): void => {
    trackEvent('edit_item', { item_category: entity })
}

export const trackDelete = (entity: EntityType): void => {
    trackEvent('delete_item', { item_category: entity })
}

export const trackRestore = (entity: EntityType): void => {
    trackEvent('restore_item', { item_category: entity })
}

export const trackLogin = (method: string): void => {
    trackEvent('login', { method })
}

export const trackSignUp = (method: string): void => {
    trackEvent('sign_up', { method })
}

export const trackLogout = (): void => {
    trackEvent('logout')
}

export const trackStockAdjustment = (actionType: StockAdjustmentType): void => {
    trackEvent('adjust_stock', { action_type: actionType })
}

export const trackInvitationResponse = (accepted: boolean): void => {
    trackEvent(accepted ? 'accept_invitation' : 'reject_invitation', {
        item_category: 'access_invitation',
    })
}
